import { useNavigation } from "@react-navigation/native";
import { useCallback, useEffect, useMemo } from "react";
import { FlatList, ListRenderItem } from "react-native";

import { Callout, CalloutRefresh, ChatItem } from "../components";
import { Chat, ChatMessage, useChats, useUser } from "../services";
import { LoadingScreen } from "./loading";

function getUnreadCount(messages: ChatMessage[]) {
  return messages.filter((m) => !m.model_id && !m.read).length;
}

export function ChatsScreen() {
  // Data
  const {
    data: user,
    isLoading: isUserLoading,
    refetch: refetchUser,
  } = useUser();
  const {
    data: chats,
    isLoading: isChatsLoading,
    refetch: refetchChats,
  } = useChats();

  // Vars
  const navigation = useNavigation();

  const totalUnreadCount = useMemo(() => {
    return (chats ?? []).reduce((acc, { messages }) => {
      return acc + getUnreadCount(messages);
    }, 0);
  }, [chats]);

  // Methods
  const refresh = useCallback(() => {
    refetchUser();
    refetchChats();
  }, [refetchUser, refetchChats]);

  // Handlers
  const goToChat = useCallback(
    (id: number, userName: string) => {
      navigation.navigate("Chat", { id, userName });
    },
    [navigation]
  );

  const goToPremium = useCallback(() => {
    navigation.navigate("Premium");
  }, [navigation]);

  const renderListItem = useCallback(
    ({ id, blocked, user, messages }: Chat) => {
      const message = messages[0];
      const unreadCount = getUnreadCount(messages);

      return (
        <ChatItem
          disabled={blocked}
          key={id}
          id={id}
          userId={user.id}
          userName={user.name}
          userScore={user.score}
          unread={unreadCount}
          onPress={goToChat}
          message={message ? message.text : undefined}
        />
      );
    },
    [goToChat]
  );

  const renderItem: ListRenderItem<Chat> = useCallback(
    ({ item }) => renderListItem(item),
    [renderListItem]
  );

  // Effects
  useEffect(() => {
    navigation.setOptions({
      tabBarBadge: totalUnreadCount || undefined,
    });
  }, [navigation, totalUnreadCount]);

  if (isUserLoading || isChatsLoading) {
    return <LoadingScreen />;
  }

  if (!user || !chats) {
    return <CalloutRefresh onRefresh={refresh} />;
  }

  if (!user.premium) {
    return (
      <Callout
        icon="chat-outline"
        message="To see your chats you need a Premium subscription"
        action="Buy Premium"
        onPress={goToPremium}
      />
    );
  }

  return (
    <FlatList<Chat>
      data={chats}
      renderItem={renderItem}
      ListEmptyComponent={
        <Callout
          icon="chat-processing-outline"
          message="You have no active chat"
        />
      }
    />
  );
}
